import React, { useEffect, useRef, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Plus, Trash2, ChevronUp, ChevronDown, Wand2, Loader2, ListChecks, GripVertical, PenSquare,
  Target, BookOpen, Layers,
} from "lucide-react";
import { toast } from "sonner";
import AutoGrowTextarea from "@/components/AutoGrowTextarea";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));
const normalize = (b) => (Array.isArray(b) ? b : []);

export default function BeatPlanner({ open, onOpenChange, chapter, act, onUpdate, api, projectId, logline, onWrite, chapters }) {
  const [beats, setBeats] = useState([]);
  const [ai, setAi] = useState(false);
  const commitTimer = useRef(null);

  useEffect(() => {
    if (open && chapter) setBeats(normalize(chapter.beats));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, chapter?.id]);

  const commit = (list) => onUpdate({ beats: list });

  // Flush any pending debounced text edit when the drawer closes.
  useEffect(() => {
    if (!open && commitTimer.current) {
      clearTimeout(commitTimer.current);
      commitTimer.current = null;
      if (chapter) onUpdate({ beats });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const update = (list, immediate = true) => {
    setBeats(list);
    if (immediate) { commit(list); return; }
    if (commitTimer.current) clearTimeout(commitTimer.current);
    commitTimer.current = setTimeout(() => commit(list), 600);
  };

  const addBeat = () => update([...beats, { id: uid(), text: "", done: false }]);
  const setText = (id, text) => update(beats.map((b) => (b.id === id ? { ...b, text } : b)), false);
  const toggle = (id) => update(beats.map((b) => (b.id === id ? { ...b, done: !b.done } : b)));
  const remove = (id) => update(beats.filter((b) => b.id !== id));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= beats.length) return;
    const next = [...beats];
    [next[i], next[j]] = [next[j], next[i]];
    update(next);
  };

  const suggest = async () => {
    setAi(true);
    try {
      const all = Array.isArray(chapters) ? [...chapters].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) : [];
      const idx = all.findIndex((c) => c.id === chapter.id);
      const slim = (c) => ({ title: c.title || "", summary: c.summary || "" });
      const prior = idx > 0 ? all.slice(0, idx).map(slim) : [];
      const next = idx >= 0 ? all.slice(idx + 1).map(slim) : [];
      const { data } = await api.post(`/projects/${projectId}/blueprint/expand`, {
        mode: "beats", logline: logline || "",
        chapter_title: chapter.title || "", chapter_summary: chapter.summary || "", count: 5,
        prior_chapters: prior, next_chapters: next,
      });
      const added = (data.suggestions || []).map((s) => ({ id: uid(), text: s.text, done: false }));
      update([...beats, ...added]);
      toast.success(`${added.length} beats drafted`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI planning failed");
    } finally {
      setAi(false);
    }
  };

  const doneCount = beats.filter((b) => b.done).length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-background p-0 flex flex-col" data-testid="beat-planner">
        <SheetHeader className="p-5 pb-4 border-b border-border text-left">
          <div className="flex items-center gap-2 text-primary mb-1">
            <ListChecks className="w-4 h-4" />
            <span className="text-xs uppercase tracking-widest font-semibold">Beat / Scene Planner</span>
          </div>
          <SheetTitle className="font-display text-2xl leading-tight">{chapter?.title || "Chapter"}</SheetTitle>
          <SheetDescription>
            Detail-plan this chapter just before you write it — list the beats/scenes in order so you always have a plan to work towards.
          </SheetDescription>
        </SheetHeader>

        <BlueprintContext logline={logline} act={act} summary={chapter?.summary} />

        <div className="flex items-center gap-2 px-5 py-3 border-b border-border">
          <Button size="sm" variant="outline" onClick={suggest} disabled={ai} data-testid="beat-ai-suggest" className="gap-2">
            {ai ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} AI: Suggest beats
          </Button>
          {beats.length > 0 && (
            <span className="ml-auto text-xs text-muted-foreground">{doneCount}/{beats.length} done</span>
          )}
        </div>

        <ScrollArea className="flex-1">
          <div className="p-4 space-y-2">
            {beats.length === 0 ? (
              <div className="text-center py-14 text-muted-foreground">
                <ListChecks className="w-9 h-9 mx-auto mb-3 opacity-40" />
                <p className="text-sm mb-4">No beats yet. Add them yourself or let AI draft a plan.</p>
              </div>
            ) : beats.map((b, i) => (
              <div key={b.id} data-testid={`beat-row-${b.id}`}
                className="group flex items-start gap-2 rounded-lg border border-border bg-card p-2.5">
                <div className="flex flex-col items-center pt-0.5 gap-1">
                  <span className="text-xs font-mono-code text-muted-foreground w-5 text-center">{i + 1}</span>
                  <Checkbox checked={b.done} onCheckedChange={() => toggle(b.id)} data-testid={`beat-done-${b.id}`} />
                </div>
                <AutoGrowTextarea value={b.text} onChange={(e) => setText(b.id, e.target.value)}
                  minRows={2} placeholder="Describe this beat/scene…"
                  data-testid={`beat-text-${b.id}`}
                  className={`flex-1 text-sm bg-transparent outline-none leading-snug ${b.done ? "line-through text-muted-foreground" : ""}`} />
                <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => move(i, -1)} className="text-muted-foreground hover:text-foreground" data-testid={`beat-up-${b.id}`}><ChevronUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => move(i, 1)} className="text-muted-foreground hover:text-foreground" data-testid={`beat-down-${b.id}`}><ChevronDown className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(b.id)} className="text-muted-foreground hover:text-destructive" data-testid={`beat-delete-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
            <button onClick={addBeat} data-testid="add-beat-button"
              className="w-full rounded-lg border border-dashed border-border hover:border-primary/50 text-muted-foreground hover:text-primary transition-colors py-2.5 text-sm flex items-center justify-center gap-1.5">
              <Plus className="w-4 h-4" /> Add beat
            </button>
          </div>
        </ScrollArea>

        {onWrite && (
          <div className="p-4 border-t border-border">
            <Button onClick={() => onWrite(chapter.id)} className="w-full gap-2" data-testid="beat-write-button">
              <PenSquare className="w-4 h-4" /> Write this chapter
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}


function ContextRow({ icon: Icon, label, value, muted, mono }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">{label}</div>
        <div className={`text-sm leading-snug ${muted ? "text-muted-foreground italic" : "text-foreground"} ${mono ? "font-mono-code" : ""}`}>
          {value}
        </div>
      </div>
    </div>
  );
}

function BlueprintContext({ logline, act, summary }) {
  const hasAny = (logline && logline.trim()) || act || (summary && summary.trim());
  return (
    <div className="px-5 py-3 border-b border-border bg-secondary/30" data-testid="beat-context">
      <div className="flex items-center gap-1.5 mb-2.5">
        <BookOpen className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">From your blueprint</span>
      </div>
      {!hasAny ? (
        <p className="text-xs text-muted-foreground italic">
          No blueprint context yet — add a logline, act pitch or chapter summary in the Blueprint tab and it'll show here.
        </p>
      ) : (
        <div className="space-y-2.5">
          <ContextRow icon={Target} label="Logline"
            value={logline?.trim() || "No logline set"} muted={!logline?.trim()} />
          {act && (
            <ContextRow icon={Layers} label={`Act — ${act.title}`}
              value={act.pitch?.trim() || "No act pitch yet"} muted={!act.pitch?.trim()} />
          )}
          <ContextRow icon={ListChecks} label="This chapter"
            value={summary?.trim() || "No chapter summary yet"} muted={!summary?.trim()} />
        </div>
      )}
    </div>
  );
}
